import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/Button";
import { Stack } from "@/components/ui/Stack";
import { LinkIcon, TerminalIcon, UploadIcon } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import { FullWidthUpload } from "@/components/upload";
import { Spinner } from "@/components/ui/Spinner";
import { useMutation } from "@tanstack/react-query";
import { useSdkClient } from "@/contexts/Sdk";
import { UploadOpenAPIv3Result } from "@gram/client/models/components/uploadopenapiv3result.js";
import { CodeBlock } from "@/components/code";
import { shellQuote } from "@/lib/shell";

interface OpenApiSourceInputProps {
  onUpload: (file: File) => void;
  onUrlUpload?: (result: UploadOpenAPIv3Result) => void | Promise<void>;
  /** When provided, shows a CLI tab with pre-filled commands for this document slug. */
  documentSlug?: string;
  /** The document's display name, kept by the CLI commands; the slug otherwise. */
  documentName?: string;
  className?: string;
  isLoading?: boolean;
}

export function OpenApiSourceInput({
  onUpload,
  onUrlUpload,
  documentSlug,
  documentName,
  className,
  isLoading,
}: OpenApiSourceInputProps): JSX.Element {
  const [url, setUrl] = useState("");
  // Both CLI commands fall back to the slug as the name when none is given,
  // which would rename a document whose name differs from its slug.
  const cliName = documentName ?? documentSlug ?? "";
  const client = useSdkClient();

  const fetchMutation = useMutation({
    mutationFn: async (urlToFetch: string) => {
      const result = await client.assets.fetchOpenAPIv3FromURL({
        fetchOpenAPIv3FromURLForm2: {
          url: urlToFetch,
        },
      });
      return result;
    },
    onSuccess: async (result) => {
      if (onUrlUpload) {
        await onUrlUpload(result);
      } else {
        // Fallback: create a placeholder file for compatibility
        const filename = url.split("/").pop() || "openapi.yaml";
        const placeholderFile = new File([], filename, {
          type: "application/yaml",
        });
        onUpload(placeholderFile);
      }
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Failed to fetch URL",
      );
    },
  });

  const handleSubmit = () => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      toast.error("Please enter a URL");
      return;
    }

    try {
      new URL(trimmedUrl);
    } catch {
      toast.error("Please enter a valid URL");
      return;
    }

    fetchMutation.mutate(trimmedUrl);
  };

  return (
    <Tabs defaultValue="upload" className={className}>
      <TabsList
        className={`grid w-full ${documentSlug ? "grid-cols-3" : "grid-cols-2"}`}
      >
        <TabsTrigger value="upload">
          <UploadIcon className="mr-1.5 size-4" />
          Upload
        </TabsTrigger>
        <TabsTrigger value="url">
          <LinkIcon className="mr-1.5 size-4" />
          From URL
        </TabsTrigger>
        {documentSlug && (
          <TabsTrigger value="cli">
            <TerminalIcon className="mr-1.5 size-4" />
            CLI
          </TabsTrigger>
        )}
      </TabsList>
      <TabsContent value="upload" className="my-3">
        <FullWidthUpload
          onUpload={onUpload}
          className="max-w-full"
          allowedExtensions={["yaml", "yml", "json"]}
          isLoading={isLoading}
        />
      </TabsContent>
      <TabsContent value="url" className="my-3 flex flex-col justify-center">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          className="flex h-full flex-col justify-center"
        >
          <Stack gap={3}>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/openapi.yaml"
              className="border-input bg-background focus:ring-ring w-full border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
              disabled={fetchMutation.isPending}
              required
            />
            <Button
              type="submit"
              disabled={!url.trim() || fetchMutation.isPending}
              className="w-full"
            >
              {fetchMutation.isPending && (
                <Button.LeftIcon>
                  <Spinner className="size-4" />
                </Button.LeftIcon>
              )}
              <Button.Text>
                {fetchMutation.isPending ? "Loading..." : "Load OpenAPI Spec"}
              </Button.Text>
            </Button>
          </Stack>
        </form>
      </TabsContent>
      {documentSlug && (
        <TabsContent value="cli" className="my-3 space-y-3">
          <div>
            <p className="text-muted-foreground mb-1.5 text-xs">
              Direct upload
            </p>
            <CodeBlock language="bash" className="!bg-muted/50 !border-0">
              {`speakeasy upload --type openapiv3 \\\n  --slug ${documentSlug} \\\n  --name ${shellQuote(cliName)} \\\n  --location ./path/to/spec.yaml`}
            </CodeBlock>
          </div>
          <div>
            <p className="text-muted-foreground mb-1.5 text-xs">
              Or stage and push (useful for CI/CD)
            </p>
            <CodeBlock language="bash" className="!bg-muted/50 !border-0">
              {`speakeasy stage openapi \\\n  --slug ${documentSlug} \\\n  --name ${shellQuote(cliName)} \\\n  --location ./path/to/spec.yaml\n\nspeakeasy push`}
            </CodeBlock>
          </div>
        </TabsContent>
      )}
    </Tabs>
  );
}
